import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import GlassUtils from '../../utils/GlassUtils';

const VIEW_KEY = 'subMatFolderGradeView';

/** 유리 구성 칸과 같은 40px. */
const inlineFieldSx = {
  '& .MuiInputLabel-root': { fontSize: '0.9rem' },
  '& .MuiOutlinedInput-root': { height: 40 },
  '& .MuiInputBase-input': { fontSize: '0.9rem', py: 0 },
  '& .MuiAutocomplete-input': { py: '0 !important' },
};

const readView = () => {
  try {
    return localStorage.getItem(VIEW_KEY) === 'columns' ? 'columns' : 'combo';
  } catch (_err) {
    return 'combo';
  }
};

const partsFromRow = (row) => {
  const detail = row?.detail || {};
  const keys = Object.keys(detail)
    .filter((key) => /^type\d+$/.test(key))
    .sort((a, b) => Number(a.slice(4)) - Number(b.slice(4)));
  if (keys.length) return keys.map((key) => (detail[key] == null ? '' : String(detail[key])));
  const divider = row?.divider || ' ';
  return row?.type ? String(row.type).split(divider) : [];
};

const sameParts = (row, parts) => {
  if (!Array.isArray(parts) || !parts.length) return false;
  const rowParts = partsFromRow(row);
  return rowParts.length === parts.length && rowParts.every((part, index) => part === String(parts[index] ?? ''));
};

const rowType = (row) => row?.type || partsFromRow(row).join(row?.divider || ' ');

/** 유리 구성이 가리키는 문자열과 같거나, 그보다 긴 조합만 남긴다. */
const allowedRows = (rows, hint, manual) => {
  if (manual || !hint) return rows;
  return rows.filter((row) => rowType(row).substring(0, hint.length) === hint);
};

const exactParts = (rows, hint) => {
  if (!hint) return [];
  const exact = rows.find((row) => rowType(row) === hint);
  return exact ? partsFromRow(exact) : [];
};

const optionsAt = (rows, index, prefix) => {
  const values = new Set();
  rows.forEach((row) => {
    const parts = partsFromRow(row);
    if (!prefix.every((part, i) => parts[i] === part)) return;
    if (parts.length <= index) values.add('');
    else values.add(parts[index]);
  });
  return [...values];
};

/** 모든 후보가 같은 값인 앞 열은 고정한다. */
const fixedPrefix = (rows) => {
  const count = rows.reduce((max, row) => Math.max(max, partsFromRow(row).length), 0);
  const fixed = [];
  for (let index = 0; index < count; index += 1) {
    const options = optionsAt(rows, index, fixed);
    if (options.length === 1 && options[0] !== '') fixed.push(options[0]);
    else break;
  }
  return fixed;
};

const columnCount = (rows) => rows.reduce((max, row) => Math.max(max, partsFromRow(row).length), 0);

const resolveParts = (rows, chosen) => {
  const normalized = [...chosen];
  while (normalized.length && normalized[normalized.length - 1] === '') normalized.pop();
  const matches = rows.filter((row) => sameParts(row, normalized));
  return matches.length === 1 ? partsFromRow(matches[0]) : null;
};

const SubMatFolderClassification = ({
  certId,
  classifications,
  isProcess,
  certCode,
  glassItems,
  savedParts,
  onChange,
  inline = false,
}) => {
  const [rows, setRows] = useState([]);
  const [manual, setManual] = useState(false);
  const [selectedParts, setSelectedParts] = useState([]);
  const [view, setView] = useState(readView);
  const manualRef = useRef(false);
  const baseRef = useRef([]);

  const glassHint = () => {
    if (!isProcess) return '';
    return GlassUtils.getClassification({
      type: certCode,
      specificationElements: [],
      selectedGcomponentItems: glassItems || [],
    }) || '';
  };

  const publish = (parts) => {
    setSelectedParts(parts);
    onChange?.(parts);
  };

  useEffect(() => {
    const base = Array.isArray(classifications) ? classifications : [];
    baseRef.current = base;
    setRows(base);
    if (!base.length) {
      publish([]);
      return;
    }
    const hint = glassHint();
    const auto = exactParts(base, hint);
    const saved = Array.isArray(savedParts) ? savedParts : [];
    const savedRow = base.find((row) => sameParts(row, saved));
    const savedType = savedRow ? rowType(savedRow) : '';
    const autoType = auto.length ? hint : '';
    if (!isProcess && base.length === 1) {
      manualRef.current = true;
      setManual(true);
      publish(partsFromRow(base[0]));
      return;
    }
    const useManual = !isProcess || (Boolean(savedType) && savedType !== autoType);
    manualRef.current = useManual;
    setManual(useManual);
    publish(useManual ? (savedRow ? partsFromRow(savedRow) : []) : auto);
  }, [certId]);

  useEffect(() => {
    if (!isProcess || manualRef.current || !baseRef.current.length) return;
    publish(exactParts(baseRef.current, glassHint()));
  }, [glassItems, isProcess, certCode]);

  if (!rows.length) return null;

  const hint = isProcess && !manual ? glassHint() : '';
  const candidates = allowedRows(rows, hint, manual || !isProcess);
  const fixed = fixedPrefix(candidates);
  const chosen = selectedParts.length ? selectedParts : fixed;
  const openCount = columnCount(candidates);
  const single = candidates.length === 1 ? candidates[0] : null;
  const selectedRow = candidates.find((row) => sameParts(row, selectedParts)) || null;

  const handleView = (_event, next) => {
    if (!next) return;
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch (_err) {
      // 저장이 막혀도 이번 화면의 보기는 유지한다.
    }
  };

  const handleManual = (checked) => {
    manualRef.current = checked;
    setManual(checked);
    if (checked) {
      publish([]);
      return;
    }
    publish(exactParts(baseRef.current, glassHint()));
  };

  const handleCombo = (_event, row) => {
    publish(row ? partsFromRow(row) : []);
  };

  const handleColumn = (index, value) => {
    const base = selectedParts.length >= index ? selectedParts : fixed;
    const next = base.slice(0, index);
    next[index] = value;
    while (next.length && next[next.length - 1] === '') next.pop();
    const resolved = resolveParts(candidates, next);
    setSelectedParts(resolved || next);
    onChange?.(resolved || []);
  };

  if (inline) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
        {candidates.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            유리 구성에 맞는 등급이 없습니다.
          </Typography>
        )}
        {single && (
          <TextField
            size="small"
            label="등급"
            value={rowType(single)}
            InputProps={{ readOnly: true }}
            sx={{ width: 220, maxWidth: '100%', ...inlineFieldSx }}
          />
        )}
        {!single && candidates.length > 0 && (
          <Autocomplete
            size="small"
            options={candidates}
            value={selectedRow}
            onChange={handleCombo}
            getOptionLabel={rowType}
            isOptionEqualToValue={(left, right) => rowType(left) === rowType(right)}
            renderInput={(params) => <TextField {...params} label="등급" />}
            sx={{ width: 260, maxWidth: '100%', ...inlineFieldSx }}
            componentsProps={{
              paper: { sx: { width: 'max-content', minWidth: '100%', maxWidth: 640 } },
            }}
          />
        )}
        {isProcess && (
          <FormControlLabel
            sx={{ m: 0, height: 40, alignItems: 'center' }}
            control={<Checkbox size="small" checked={manual} onChange={(e) => handleManual(e.target.checked)} />}
            label="등급 수동설정"
          />
        )}
      </Stack>
    );
  }

  return (
    <Card variant="outlined">
      <CardHeader
        title="종류·등급·호칭 또는 모델"
        titleTypographyProps={{ variant: 'subtitle1' }}
        sx={{ bgcolor: '#eaeaea', py: 1 }}
      />
      <CardContent sx={{ pt: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={view}
            onChange={handleView}
          >
            <ToggleButton value="combo">전체 등급</ToggleButton>
            <ToggleButton value="columns">항목별</ToggleButton>
          </ToggleButtonGroup>
          {isProcess && (
            <FormControlLabel
              sx={{ mr: 0 }}
              control={<Checkbox size="small" checked={manual} onChange={(e) => handleManual(e.target.checked)} />}
              label="등급 수동설정"
            />
          )}
        </Stack>
        {candidates.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            유리 구성에 맞는 등급이 없습니다.
          </Typography>
        )}
        {view === 'combo' && single && (
          <TextField
            size="small"
            label="전체 등급"
            value={rowType(single)}
            InputProps={{ readOnly: true }}
            sx={{ width: 420, maxWidth: '100%' }}
          />
        )}
        {view === 'combo' && !single && candidates.length > 0 && (
          <Autocomplete
            size="small"
            options={candidates}
            value={selectedRow}
            onChange={handleCombo}
            getOptionLabel={rowType}
            isOptionEqualToValue={(left, right) => rowType(left) === rowType(right)}
            renderInput={(params) => <TextField {...params} label="전체 등급" />}
            sx={{ width: 420, maxWidth: '100%' }}
            componentsProps={{
              paper: { sx: { width: 'max-content', minWidth: '100%', maxWidth: 640 } },
            }}
          />
        )}
        {view === 'columns' && candidates.length > 0 && (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {fixed.map((text, index) => (
              <TextField
                key={`fixed-${index}`}
                size="small"
                value={text}
                InputProps={{ readOnly: true }}
                sx={{ width: 220 }}
              />
            ))}
            {Array.from({ length: Math.max(0, openCount - fixed.length) }, (_, offset) => fixed.length + offset)
              .filter((index) => index === 0 || chosen.length >= index)
              .map((index) => {
                const options = optionsAt(candidates, index, chosen.slice(0, index));
                if (!options.length) return null;
                const current = selectedParts[index] ?? '';
                const value = options.includes(current) ? current : '';
                return (
                  <FormControl key={`col-${index}`} size="small" sx={{ width: 220 }}>
                    <InputLabel id={`folder-grade-col-${index}`}>선택</InputLabel>
                    <Select
                      labelId={`folder-grade-col-${index}`}
                      label="선택"
                      value={value}
                      onChange={(e) => handleColumn(index, e.target.value)}
                    >
                      {options.map((option) => (
                        <MenuItem key={option || 'none'} value={option}>{option || '없음'}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default SubMatFolderClassification;

/**
 * 레거시 성적서 prepareTestComparisonData와 같이 유리 구성에서 두께를 만든다.
 * KS L 2004는 유리·필름 층 두께를 `_`로 잇고, KS L 2003은 중공 2겹 또는 유리 15mm 이상이면 -1.
 */
export const processGlassLead = (selectedGcomponentItems, gstandardNo) => {
  let thickness = 0;
  let spacerCount = 0;
  let maxGlassThickness = 0;
  const ks2004 = [];
  (selectedGcomponentItems || []).forEach((layer) => {
    (layer || []).forEach((item, index) => {
      if (index === 0 || !item) return;
      const code = item.code;
      if (code !== 'GL THICKNESS' && code !== 'SPACER THICKNESS' && code !== 'FILM THICKNESS') return;
      const valueCode = item.value?.code;
      const amount = Number(valueCode);
      if (Number.isNaN(amount)) return;
      thickness = Number((thickness + amount).toFixed(2));
      if (gstandardNo === 'KS L 2003') {
        if (code === 'SPACER THICKNESS') spacerCount += 1;
        else if (code === 'GL THICKNESS') maxGlassThickness = Math.max(maxGlassThickness, amount);
      } else if (gstandardNo === 'KS L 2004' && (code === 'GL THICKNESS' || code === 'FILM THICKNESS')) {
        ks2004.push(String(valueCode));
      }
    });
  });
  const summed = thickness > 0 ? String(thickness) : '';
  const except = gstandardNo === 'KS L 2003' && (spacerCount >= 2 || maxGlassThickness >= 15);
  return {
    thickness: except ? '-1' : summed,
    ftThickness: summed,
    ks2004_thickness: ks2004.length ? ks2004.join('_') : '',
  };
};

/** 레거시 가공 성적서와 같이 층 두께를 더한다. */
export const sumProcessThickness = (selectedGcomponentItems, gstandardNo) => (
  processGlassLead(selectedGcomponentItems, gstandardNo).thickness
);
